import React, { useEffect, useState, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { formatDistanceToNow } from 'date-fns';
import OptionToRevisitStudiesModal from '../../OptionToRevisitStudiesModal';
import OutcomeGroupsSection from './components/OutcomeGroupsSection';
import DomainsSection from './components/DomainsSection';
import * as styles from './DefaultTemplate.module.scss';
import useCookie from 'hooks/useCookie';
import { studyListCookies } from 'pages/StudyList/studyListCookies';
import { useConfirmUnsaved } from 'hooks/useConfirmUnsaved';
import { Button, Icon, IconList, Link, Text } from 'components/core';
import { IsolationPage, TemplateToolInfo } from 'components/shared';
import {
  EXTRACTION_STUDIES_LIST_ROUTE,
  QUALITY_ASSESSMENT_TEMPLATE_CREATE_ROUTE,
  tokenizeRoute,
} from 'query/routes';
import {
  createEmptyQATemplate,
  getQATemplateProps,
  getQATemplate,
  getReviewId,
  updateQATemplate,
  getHttpErrorMessage,
} from 'query/review';
import {
  QualityAssessmentTemplateFormat,
  QualityAssessmentTemplate,
} from 'types/DataExtraction';
import {
  createToast,
  getFocusedElement,
  Toast,
  ToastButton,
} from 'components/core/Toast';

const getBackRoute = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const backToParam = urlParams.get('backTo');
  const typeParam = urlParams.get('type');

  if (backToParam) {
    return tokenizeRoute(QUALITY_ASSESSMENT_TEMPLATE_CREATE_ROUTE, {
      review_id: getReviewId(),
      backTo: backToParam,
      ...(typeParam && { type: typeParam }),
    });
  }

  return tokenizeRoute(EXTRACTION_STUDIES_LIST_ROUTE, {
    review_id: getReviewId(),
  });
};

type Props = {
  getTemplate?: ({
    initialFormat,
  }: getQATemplateProps) => Promise<QualityAssessmentTemplate>;
  updateTemplate?: (
    template: QualityAssessmentTemplate,
    revisitStudies: boolean
  ) => Promise<Response>;
  initialFormat: QualityAssessmentTemplateFormat;
};

const DefaultTemplate = ({
  getTemplate = getQATemplate,
  updateTemplate = updateQATemplate,
  initialFormat,
}: Props) => {
  const backRoute = getBackRoute();
  const publishRoute = tokenizeRoute(EXTRACTION_STUDIES_LIST_ROUTE, {
    review_id: getReviewId(),
  });
  const [isLoading, setLoading] = useState(true);
  const [revisitModal, setRevisitModal] = useState<React.ReactElement | null>();
  const revisitStudies = useRef(false);
  const [, saveQaPublishCookie] = useCookie<boolean>(
    studyListCookies.qaTemplatePublished,
    false
  );
  const [, saveQaMovedBackCookie] = useCookie<boolean>(
    studyListCookies.qaMovedBack,
    false
  );
  const formFn = useForm<QualityAssessmentTemplate>({
    mode: 'all',
    defaultValues: createEmptyQATemplate(),
    shouldFocusError: false,
  });

  const {
    formState: { isDirty, isValid },
  } = formFn;

  const [template, setTemplate] = useState<QualityAssessmentTemplate>();
  useEffect(() => {
    let isMounted = true;

    getTemplate({ initialFormat })
      .then((loadedTemplate) => {
        if (!isMounted) return;
        setTemplate(loadedTemplate);
        // Keep the form hydration in the same async flow to avoid
        // user/test interactions being overwritten by a late reset
        formFn.reset(loadedTemplate);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [formFn, getTemplate, initialFormat]);

  const publishTemplate = formFn.handleSubmit(
    (state) => {
      // Captured up front: submission can come from the Publish button or
      // from pressing Enter in a field, so there's no single button ref to
      // rely on - and by the time the save resolves, the Publish button
      // (disabled while submitting) will have already lost focus.
      const triggerElement = getFocusedElement();

      setLoading(true);
      updateTemplate(state, revisitStudies.current)
        .then((response) => {
          if (response.status !== 200) {
            createToast(
              <Toast
                type="danger"
                focusOnAppear
                restoreFocusTo={triggerElement}
              >
                {getHttpErrorMessage(response.status)}
                <ToastButton
                  to={'mailto:support@covidence.org'}
                  target="blank"
                  rel="noopener noreferrer"
                >
                  Need help?
                </ToastButton>
              </Toast>,
              { duration: 6000 }
            );
            return;
          }

          response.json().then((data) => {
            clean();
            window.location.href = publishRoute.toString();

            if (data.moved_back) {
              saveQaMovedBackCookie(true, 'session');
            }

            saveQaPublishCookie(true, 'session');
            return;
          });
        })
        .finally(() => setLoading(false));
    },
    () => {
      const invalidInput = document.querySelector(
        '[aria-invalid=true]'
      ) as HTMLElement;
      invalidInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => invalidInput?.focus(), 1000);
    }
  );

  const [clean, dirty] = useConfirmUnsaved();
  useEffect(() => {
    isDirty ? dirty() : clean();
  }, [isDirty, dirty, clean]);

  const shouldShowRevisitModal = (): boolean => {
    return (
      (template?.number_of_revisitable_studies || 0) > 0 && isValid && isDirty
    );
  };

  const onPublish = (revisit = false) => {
    revisitStudies.current = revisit;
    publishTemplate();
  };

  const showOptionToRevisitModal = () => {
    setRevisitModal(
      <OptionToRevisitStudiesModal
        numberOfRevisitableStudies={
          template?.number_of_revisitable_studies || 0
        }
        onClose={() => setRevisitModal(null)}
        onPublish={onPublish}
        templateType="QualityAssessment"
      />
    );
  };

  const publishHandler = () => {
    if (shouldShowRevisitModal()) {
      return showOptionToRevisitModal;
    } else {
      return publishTemplate;
    }
  };

  return (
    <FormProvider {...formFn}>
      {revisitModal}
      <IsolationPage
        back={backRoute}
        title={
          <TemplateToolInfo
            title="Quality assessment template"
            subTitle="Extraction 1"
            icon={IconList.light.faTableList}
          />
        }
        controls={
          <>
            {template?.updated_at && (
              <Text variant="medium" size="xs">
                Published{' '}
                {formatDistanceToNow(Date.parse(template?.updated_at), {
                  addSuffix: true,
                })}{' '}
                by {template?.updated_by?.first_name}
              </Text>
            )}
            <Button
              onClick={publishHandler()}
              type="brand"
              disabled={isLoading}
              isLoading={isLoading}
              data-pendo-key="qat-publish"
            >
              Publish template
            </Button>
            <Link
              className={styles.HelpLink}
              onClick={() => ''}
              data-pendo-key="qat-help"
              aria-label="Get help"
            >
              <Icon icon={IconList.light.faQuestionCircle} />
            </Link>
          </>
        }
      >
        <div className={styles.DefaultTemplate}>
          <DomainsSection />
          <OutcomeGroupsSection />
        </div>
      </IsolationPage>
    </FormProvider>
  );
};

export default DefaultTemplate;
